import React, { Component } from "react";
import ReactDOM from "react-dom";
import { setAndGetAttributes } from "../../FormInputFields/FormInputFunctions";
import Wrp from "../../../hoc/Wrp/Wrp";
import Autocomplete from "../../AutoComplete";
import { callGET } from "../../../Utils/ApiUtil";
import { IsJsonString, isSet } from "../../../Utils/CommonFunctions";
import Icons from "../../Icons/Icons";
import WindowIcons from "../../Icons/WindowIcon";
import { getProfileAuthorities, getWindowIcons } from "../../../Utils/UserFunctions";
import PagesServices from "../../../services/PagesServices";
import http from "../../../services/http";
import {
  extractLookupIcon,
  extractConfigAttributes,
  buildLookupSelection,
  buildLookupExecutePayload,
  hasFilterPayloadChanged,
  getLookupStoredLabel,
  getLookupStoredValue,
  renderNodeChildren,
} from "../../../Containers/PagesContainer/PagesContainerUtils";
import LookupModal from "./LookupModal";
import { connect } from "react-redux";
import * as actions from "../../../store/actions";

// From and To address both refetch when Account changes. Keep the page
// loader up until every one of those page-config calls has finished.
let pagesLookupFilterLoaderCount = 0;

const beginPagesLookupFilterLoader = (Loader) => {
  pagesLookupFilterLoaderCount += 1;
  if (typeof Loader === "function") {
    Loader(true);
  }
};

const endPagesLookupFilterLoader = (Loader) => {
  pagesLookupFilterLoaderCount = Math.max(0, pagesLookupFilterLoaderCount - 1);
  if (pagesLookupFilterLoaderCount === 0 && typeof Loader === "function") {
    Loader(false);
  }
};

class PagesLookup extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.packagesControlRef = React.createRef();
    this.packagesSearchRef = React.createRef();
    this.packagesMenuId =
      "scm-packages-lookup-menu-" +
      String(
        props.name || (props.attributes && props.attributes.name) || "pkg"
      ).replace(/\W/g, "_");
    this.state = {
      isModalOpen: false,
      isLoading: false,
      displayName: "",
      lookupAutoComplete: [],
      name: "",
      typing: false,
      typingTimeout: 0,
      // Packages subform only: layout CustomAll-style menu (portaled outside table)
      packagesMenuOpen: false,
      packagesFilterText: "",
      packagesMenuRect: null,
      config: {
        apiAction: "",
        searchField: "",
        offset: "",
        limit: "",
        resultVar: "",
        recordIdField: "",
        defaultField: "",
        resultFields: [], // Array of {key, label} objects
      },
      pageConfig: null,
      inputVariables: [],
      outputVariables: [],
      pageConfigId: null,
    };
  }

  isPackagesLookupField = () => {
    const { attributes } = this.props;
    const windowName =
      this.props.windowName ||
      this.props.windowname ||
      (attributes && (attributes.windowName || attributes.windowname)) ||
      "";
    const fieldName = String(
      this.props.name || (attributes && attributes.name) || ""
    ).toLowerCase();
    const fillAttr = this.props.fill || (attributes && attributes.fill) || "";
    return (
      String(windowName || "").toLowerCase() === "packages" ||
      fieldName === "cf_packages" ||
      (typeof fillAttr === "string" &&
        fillAttr.includes("cf_weight:weight") &&
        fillAttr.includes("cf_length:length"))
    );
  };

  stopPackagesEvent = (e) => {
    if (!e) return;
    // Do not preventDefault — that blocks focusing the in-menu search box.
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  updatePackagesMenuPosition = () => {
    if (!this.state.packagesMenuOpen) return;
    const el = this.packagesControlRef && this.packagesControlRef.current;
    if (!el || typeof el.getBoundingClientRect !== "function") return;
    const rect = el.getBoundingClientRect();
    this.setState({
      packagesMenuRect: {
        top: rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 220),
      },
    });
  };

  openPackagesMenu = (e) => {
    this.stopPackagesEvent(e);
    if (this.props.readOnly) return;
    this.setState(
      { packagesMenuOpen: true, packagesFilterText: "" },
      () => {
        this.updatePackagesMenuPosition();
        this.fetchLookupSuggestions("");
        if (this.packagesSearchRef && this.packagesSearchRef.current) {
          this.packagesSearchRef.current.focus();
        }
      }
    );
  };

  closePackagesMenu = () => {
    this.setState({
      packagesMenuOpen: false,
      packagesFilterText: "",
      packagesMenuRect: null,
    });
  };

  togglePackagesMenu = (e) => {
    this.stopPackagesEvent(e);
    if (this.props.readOnly) return;
    if (this.state.packagesMenuOpen) this.closePackagesMenu();
    else this.openPackagesMenu(e);
  };

  handlePackagesSearch = (e) => {
    const value = e.target.value;
    // Keep displayName as selected package; only filter the open menu list.
    this.setState({ packagesFilterText: value });
    const self = this;
    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }
    self.setState({
      name: value,
      typing: false,
      typingTimeout: setTimeout(function () {
        self.fetchLookupSuggestions(value);
      }, 300),
    });
  };

  handlePackagesOptionSelect = (e, opt) => {
    this.stopPackagesEvent(e);
    this.closePackagesMenu();
    this.autoCompleteSelect(opt || {});
  };

  handlePackagesOutsideClick = (e) => {
    if (!this.state.packagesMenuOpen) return;
    const control = this.packagesControlRef && this.packagesControlRef.current;
    const menu = document.getElementById(this.packagesMenuId);
    if (control && control.contains(e.target)) return;
    if (menu && menu.contains(e.target)) return;
    this.closePackagesMenu();
  };

  componentDidMount = () => {
    this._isMounted = true;
    // Initialize with current value if available
    const {childrenProps,value} = this.props;
    // Extract config attributes from childrenProps
    if (childrenProps) {
      const config = extractConfigAttributes(childrenProps);
      if (config) {
        this.setState({ config }, () => {
          // Fetch page config and variables once if apiAction is available
          if (isSet(config.apiAction,null) !== null) {
            this.fetchPageConfigAndVariables(config.apiAction);
          }
          // After fetching initial data, update displayName if value exists
          if (value) {
            this.updateDisplayNameFromValue(value);
          }
        });
      } else if (value) {
        // If no config, still try to update display name
        this.updateDisplayNameFromValue(value);
      }
    } else if (value) {
      this.updateDisplayNameFromValue(value);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("mousedown", this.handlePackagesOutsideClick);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", this.updatePackagesMenuPosition, true);
      window.addEventListener("resize", this.updatePackagesMenuPosition);
    }
  }

  setState(updates, callback) {
    if (!this._isMounted) {
      return;
    }
    super.setState(updates, callback);
  }

  componentWillUnmount = () => {
    this._isMounted = false;
    if (typeof document !== "undefined") {
      document.removeEventListener("mousedown", this.handlePackagesOutsideClick);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", this.updatePackagesMenuPosition, true);
      window.removeEventListener("resize", this.updatePackagesMenuPosition);
    }
    if (this.state.typingTimeout) {
      clearTimeout(this.state.typingTimeout);
    }
    if (this._filterRefetchTimer) {
      clearTimeout(this._filterRefetchTimer);
      this._filterRefetchTimer = null;
    }
  }

  componentDidUpdate = (prevProps) => {
    // Update display name when value prop changes
    const {childrenProps,value} = this.props;
    if (prevProps.value !== value) {
      // Find the label for this value from autocomplete suggestions
      if (value && value !== "null" && value !== "undefined") {
        this.updateDisplayNameFromValue(value);
      } else {
        this.setState({ displayName: "" });
      }
    }
    // Update config if childrenProps identity changed — only refetch when apiAction
    // actually changes. Parent re-renders (e.g. typing Search by ref) pass a new
    // childrenProps reference every time; that must not re-hit packages_lookup.
    if (prevProps.childrenProps !== childrenProps && childrenProps) {
      const config = extractConfigAttributes(childrenProps);
      if (config) {
        const prevApiAction = this.state.config?.apiAction;
        const nextApiAction = config.apiAction;
        const sameAction =
          String(prevApiAction || "") === String(nextApiAction || "");
        const sameFilters =
          JSON.stringify(this.state.config?.filterFields || []) ===
          JSON.stringify(config.filterFields || []);
        if (!sameAction || !sameFilters) {
          this.setState({ config }, () => {
            if (
              isSet(nextApiAction, null) !== null &&
              nextApiAction !== prevApiAction
            ) {
              this.fetchPageConfigAndVariables(nextApiAction);
            }
          });
        }
      }
    }
    const configForFilter = this.state.config?.filterFields?.length
      ? this.state.config
      : childrenProps
        ? extractConfigAttributes(childrenProps)
        : null;
    if (
      configForFilter?.filterFields?.length &&
      hasFilterPayloadChanged(
        prevProps.fieldFormPayload,
        this.props.fieldFormPayload,
        configForFilter.filterFields
      )
    ) {
      // Debounce filter-driven refetch so rapid parent form updates
      // (unrelated fields) do not stampede lookup APIs.
      if (this._filterRefetchTimer) {
        clearTimeout(this._filterRefetchTimer);
      }
      this._filterRefetchTimer = setTimeout(() => {
        this._filterRefetchTimer = null;
        if (!this._isMounted) return;
        this.setState({ isLoading: true, lookupAutoComplete: [] }, () => {
          beginPagesLookupFilterLoader(this.props.Loader);
          this.fetchLookupSuggestions(this.state.name || "", true);
        });
      }, 300);
    }
  }

  updateDisplayNameFromValue = (valueId) => {
    if (
      valueId == null ||
      valueId === "" ||
      valueId === "null" ||
      valueId === "undefined"
    ) {
      this.setState({ displayName: "" });
      return;
    }

    // Fast path: if value already has label/text, use it.
    const label = getLookupStoredLabel(valueId);
    if (label) {
      this.setState({ displayName: label });
      return;
    }

    // If value is a raw record (automation output), derive label using lookup config.
    const { config } = this.state || {};
    const defaultField = config?.defaultField || "";
    const resultFields = Array.isArray(config?.resultFields) ? config.resultFields : [];

    const tryExtractFieldValue = (obj, fieldPath) => {
      if (!obj || !fieldPath) return null;
      if (fieldPath.includes(".")) {
        const v = this.getNestedValue(obj, fieldPath);
        return v != null && v !== "" ? v : null;
      }
      if (obj[fieldPath] != null && obj[fieldPath] !== "") return obj[fieldPath];
      // case-insensitive match for simple keys
      const foundKey = Object.keys(obj).find((k) => k.toLowerCase() === String(fieldPath).toLowerCase());
      return foundKey && obj[foundKey] != null && obj[foundKey] !== "" ? obj[foundKey] : null;
    };

    const recordOrFirst =
      Array.isArray(valueId) ? valueId[0] : valueId;

    if (typeof recordOrFirst === "object" && recordOrFirst !== null) {
      let fromConfig = "";
      if (defaultField) {
        const v = tryExtractFieldValue(recordOrFirst, defaultField);
        fromConfig = v != null && v !== "" ? String(v) : "";
      }
      if (!fromConfig && resultFields.length > 0) {
        for (let i = 0; i < resultFields.length; i += 1) {
          const key = resultFields[i]?.key;
          const v = tryExtractFieldValue(recordOrFirst, key);
          if (v != null && v !== "") {
            fromConfig = String(v);
            break;
          }
        }
      }

      if (fromConfig) {
        this.setState({ displayName: fromConfig });
        return;
      }
    }

    // Fallback: use stored value to match the autocomplete list.
    const storedValue = getLookupStoredValue(valueId);
    const found = storedValue
      ? this.state.lookupAutoComplete.find((item) => item.value === storedValue)
      : null;
    if (found) this.setState({ displayName: found.label });
  };

  // Helper function to get nested property value using dot notation (e.g., "country.name")
  getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[key];
    }
    return value;
  };

  // Fetch page config and input/output variables once (not on every search)
  fetchPageConfigAndVariables = async (apiAction) => {
    try {
      // Get page config by API name
      const pageConfigResponse = await PagesServices.getConfigByName(apiAction);
      
      if (pageConfigResponse && pageConfigResponse.code === 200 && pageConfigResponse.data) {
        const pageConfig = pageConfigResponse.data;
        const pageConfigId = pageConfig.id;
        // Get input and output variables from page config
        const variablesResponse = await PagesServices.getConfigVariablesList(pageConfigId);
        
        if (variablesResponse && variablesResponse.code === 200 && variablesResponse.data) {
          const inputVariables = variablesResponse.data.content.filter(v => v.is_request === true);
          const outputVariables = variablesResponse.data.content.filter(v => v.is_response === true);
          
          this.setState({
            pageConfig,
            pageConfigId,
            inputVariables,
            outputVariables,
          }, () => {
            this.fetchLookupSuggestions('');
          });
        } else {
          console.error('Failed to get variables for page config:', pageConfigId);
        }
      } else {
        console.error('Failed to get page config by API name:', apiAction);
      }
    } catch (error) {
      console.error('Error fetching page config and variables:', error);
    }
  }


  // Fetch lookup suggestions
  fetchLookupSuggestions = async (searchValue, pageLoader = false) => {
    const { windowName, childrenProps } = this.props;
    let { config } = this.state;
    const {searchField,offset,limit} = config || {};

    // If config is not in state yet, try to extract it from childrenProps
    if (!config || (!config.apiAction && childrenProps)) {
      const extractedConfig = extractConfigAttributes(childrenProps);
      if (extractedConfig) {
        config = extractedConfig;
        this.setState({ config });
      }
    }
    const hasApiAction = config && config.apiAction && config.apiAction.trim() !== "";
    this.setState({ isLoading: true });
    
    // Use config values if available, otherwise use defaults
    const page = 0;
    const perPage = 10;
    const search = searchValue || "";
    const offsetField = offset;
    const limitField = limit;
    // Build query string with config field names
    const queryParams = new URLSearchParams();
    queryParams.append(searchField, search);
    queryParams.append(offsetField, page.toString());
    queryParams.append(limitField, perPage.toString());

    const queryString = `?${queryParams.toString()}`;

    try {
      let API_URL;
      let response;
      if (hasApiAction) {
        // Use cached page config and input variables (fetched once)
        let { pageConfigId, inputVariables, pageConfig } = this.state;
        
        if (!pageConfigId || !inputVariables || inputVariables.length === 0 || !pageConfig) {
          // If not cached yet, fetch them now (shouldn't happen, but as fallback)
          console.warn('Page config or variables not cached, fetching now...');
          await this.fetchPageConfigAndVariables(config.apiAction);
          // Get updated values from state after fetch
          pageConfigId = this.state.pageConfigId;
          inputVariables = this.state.inputVariables;
          pageConfig = this.state.pageConfig;
          
          if (!pageConfigId || !inputVariables || inputVariables.length === 0 || !pageConfig) {
            console.error('Failed to get page config or variables');
            this.setState({ isLoading: false, lookupAutoComplete: [] });
            return;
          }
        }
        
        // Get from_page_mapping from config (similar to PagesRenderComponent pattern)
        const fromMapping = pageConfig.from_page_mapping || [];
        
        // Create inputMap similar to PagesRenderComponent
        const inputMap = Object.fromEntries(
          inputVariables.map(item => [item.id, item.api_name])
        );

        const payload = buildLookupExecutePayload({
          fromMapping,
          inputMap,
          config,
          fieldFormPayload: this.props.fieldFormPayload,
          search,
          page,
          perPage,
        });
        
        // console.log('Pages API Payload:', payload);
        // Use POST method with payload - only execute API is called here
        API_URL = `pageconfig/${pageConfigId}/pages-config-execute`;
        response = await http.POST(API_URL, payload)
      }
      let listApplied = false;
      if (response.status === 200) {
        const responseData = response.data;
        if (responseData.code === 200) {
          let updateTableData = [];
          
            // Pages API response structure - extract variables from response.data.variables
            const responseVariables = responseData.data?.variables || [];
            const { outputVariables, pageConfig } = this.state;
            const { resultVar,defaultField, recordIdField, resultFields } = config || {};
            // console.log('Response variables:', responseVariables);
            // console.log('Page config:', pageConfig);
            // console.log('Config - resultVar:', resultVar, 'recordIdField:', recordIdField, 'resultFields:', resultFields);
            // console.log('Output variables:', outputVariables);
            if (responseVariables && responseVariables.length > 0 && resultVar && pageConfig) {
              const toPageMapping = pageConfig.to_page_mapping || [];
              const variableMapping = {};
              
              toPageMapping.forEach((toMap) => {
                if (toMap.field_mappings && Array.isArray(toMap.field_mappings)) {
                  toMap.field_mappings.forEach((mField) => {
                    if (mField.automation_variable_id && mField.pages_variable_name) {
                      variableMapping[mField.automation_variable_id] = mField.pages_variable_name;
                    }
                  });
                }
              });
              

              const responseVariableMap = {};
              responseVariables.forEach((responseVar) => {
                // Find the page variable name for this response variable using automation_variable_id
                const pageVariableName = variableMapping[responseVar.id];
                if (pageVariableName) {
                  responseVariableMap[pageVariableName] = responseVar;
                }
              });
              
            //   console.log('Response variable map (mapped to page variable names):', responseVariableMap);
            //   console.log('Looking for resultVar:', resultVar);
              
              // Find the result variable using pages_variable_name (e.g., "lookupObj")
              const resultVariable = responseVariableMap[resultVar];
              console.log('resultVariable (mapped by page variable name):', resultVariable);
              
              const resultOutputVariable = outputVariables.find(ov => {
                // Try to match by api_name first (if pages_variable_name is same as api_name)
                if (ov.api_name === resultVar) {
                  return true;
                }
                // Otherwise, we might need to check if there's a mapping
                // For now, try api_name match
                return false;
              });
              
              console.log('Result output variable definition:', resultOutputVariable);
              
              if (resultVariable) {
                let resRecordsData = [];
                // Try different possible locations for the value
                try{
                    const recordsData = isSet(resultVariable.value,resultVariable.selected_value);
                    resRecordsData = IsJsonString(recordsData)?JSON.parse(recordsData):recordsData;
                    if (!Array.isArray(resRecordsData)) {
                      resRecordsData = resRecordsData ? [resRecordsData] : [];
                    }
                    resRecordsData.forEach((record) => {
                        console.log('record',record,defaultField);
                        let displayLabel = '';
                        // If defaultField exists, use it (supports nested paths like "country.name")
                        if (defaultField) {
                          displayLabel = defaultField.includes('.')
                            ? this.getNestedValue(record, defaultField)
                            : record[defaultField];
                        } else if (resultFields && resultFields.length > 0) {
                          // Build label from first resultField (supports nested paths)
                          const firstField = resultFields[0];
                          displayLabel = firstField.key.includes('.')
                            ? this.getNestedValue(record, firstField.key)
                            : record[firstField.key];
                        } else if (recordIdField) {
                          // Use recordIdField (supports nested paths)
                          displayLabel = recordIdField.includes('.')
                            ? this.getNestedValue(record, recordIdField)
                            : record[recordIdField];
                        } else {
                          // Use first available field value
                          const firstKey = Object.keys(record)[0];
                          displayLabel = record[firstKey] || '';
                        }
                        
                        const selection = buildLookupSelection(
                          record,
                          recordIdField,
                          displayLabel
                        );
                        if (selection) {
                          // Packages fill="cf_weight:weight|..." and to_address need the full record.
                          const fillAttr =
                            this.props.fill ||
                            (this.props.attributes && this.props.attributes.fill) ||
                            "";
                          if (
                            fillAttr ||
                            this.props.name === "to_address" ||
                            this.isPackagesLookupField()
                          ) {
                            selection.lookupData = record;
                          }
                          updateTableData.push(selection);
                        }
                      });
                }catch(error){
                    console.error('Error parsing records data:', error);
                }
                
               
                listApplied = true;
                this.setState({ lookupAutoComplete: updateTableData, isLoading: false }, () => {
                  // After updating autocomplete data, update displayName if value exists
                  const { value } = this.props;
                  if (value) {
                    this.updateDisplayNameFromValue(value);
                  }
                });
               
              } else {
                console.warn('Result variable not found in mapped variables:', resultVar, 'Available mapped variables:', Object.keys(responseVariableMap));
              }
            } else {
              console.warn('No variables found in response or resultVar not configured:', {
                responseVariables,
                resultVar
              });
            }
         
        } else {
        //   this.setState({ lookupAutoComplete: [] });
        }
      }
      if (!listApplied) {
        this.setState({ isLoading: false });
      }
    } catch (err) {
      console.error('Lookup API error:', err);
      this.setState({ isLoading: false, lookupAutoComplete: [] });
    } finally {
      if (pageLoader) {
        endPagesLookupFilterLoader(this.props.Loader);
      }
    }
  };

  autoCompleteSelect = (obj) => {
    const { changeHandler } = this.props;
    this.setState({ displayName: obj.label });
    console.log('autoCompleteSelect called with obj:', obj);
    if (changeHandler) {
      changeHandler(obj);
    }
  };

  autoCompleteSearch = (value) => {
    console.log('autoCompleteSearch called with value:', value);
    this.setState({ displayName: value });
    const self = this;

    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }

    self.setState({
      name: value,
      typing: false,
      typingTimeout: setTimeout(function () {
        console.log('Calling fetchLookupSuggestions with value:', value);
        self.fetchLookupSuggestions(value);
      }, 300),
    });
  };

  openLoopupModalHandler = () => {
    this.setState({ isModalOpen: true });
  };

  cancelLoopupModalHandler = () => {
    this.setState({ isModalOpen: false });
  };

  setLookupValuesFromList = (lookupData) => {
    const { changeHandler } = this.props;
    this.setState({ 
      displayName: lookupData.label,
      isModalOpen: false 
    });
    if (changeHandler) {
        console.log('lookupData',lookupData);
      changeHandler(lookupData);
    }
  };


//   fieldOnChangeHandler = (e) => {
//     const { changeHandler, isEvent } = this.props;
//     if (isEvent === true) {
//       changeHandler(e.target.value, e);
//     } else {
//       changeHandler(e.target.value);
//     }
//   };

  render() {
    const { value, childrenProps, required, className, attributes } = this.props;
    const { displayName, lookupAutoComplete, isLoading, config } = this.state;
    // console.log('lookupAutoComplete',config,lookupAutoComplete);
    // Page HTML spreads windowname/windowName onto props (not nested under attributes).
    const windowName =
      this.props.windowName ||
      this.props.windowname ||
      (attributes && (attributes.windowName || attributes.windowname)) ||
      "";
    const fieldName = String(
      this.props.name || (attributes && attributes.name) || ""
    ).toLowerCase();

    let attr = setAndGetAttributes(this.props);
    const attrLocked =
      attr.readOnly === true ||
      attr.readOnly === "true" ||
      attr.disabled === true ||
      attr.disabled === "true" ||
      attr.disabled === "disabled";
    // Layout lookups keep the red bar on the icon button only.
    // The text box also receives "mandatory" from setAndGetAttributes,
    // which draws a second bar between the field and the icon.
    if (typeof attr.className === "string") {
      attr.className = attr.className.replace(/\bmandatory\b/g, "").replace(/\s+/g, " ").trim();
    }

    // Extract custom icon from children props
    let customLookupIcon = null;
    if (childrenProps) {
      customLookupIcon = extractLookupIcon(childrenProps);
    }

    // Config attributes are available in this.state.config:
    // - config.apiAction: API name from Config action attribute (e.g., "lookup_test")
    // - config.searchField: Search field name from <Search> tag (e.g., "search_input")
    // - config.offset: Offset field name from <Pagination><Offset> tag (e.g., "page_input")
    // - config.limit: Limit field name from <Pagination><Limit> tag (e.g., "limit_input")
    // - config.resultVar: Result variable name from <Result var> attribute (e.g., "lookupObj")
    // - config.recordIdField: Record ID field name from <Result recordId> attribute (e.g., "id")
    // - config.resultFields: Array of field objects from <Result><Field> tags (e.g., [{key: "CompanyName", label: "Company Name"}])

    // Same window icon the layout lookup uses (profile window_icon), not a generic info mark.
    let windowIcon = "";
    let isSystemIcon = true;
    let sourceImage = "";
    try {
      const profile = getProfileAuthorities();
      const svgIcons = getWindowIcons() || [];
      const windows = profile && profile.data && profile.data.windows ? profile.data.windows : [];
      windows.forEach((windowItem) => {
        if (windowItem.window_name === windowName && windowItem.window_icon !== undefined) {
          windowIcon = windowItem.window_icon;
          isSystemIcon = windowItem.is_system_icon;
          if (windowItem.is_system_icon === false) {
            svgIcons.forEach((svg) => {
              if (svg.id === windowItem.window_icon) {
                sourceImage = "data:image/svg+xml;base64," + svg.svg_icon;
              }
            });
          }
        }
      });
    } catch (e) {
      windowIcon = "";
    }
    const defaultIcon = windowIcon
      ? (!isSystemIcon && sourceImage
        ? <img src={sourceImage} width="16" height="16" alt="" />
        : <WindowIcons size={16} color={"#000"} windowIcon={windowIcon} />)
      : (windowName
        ? <Icons iconType={windowName} size={16} color={"#000"} />
        : <i className="fas fa-info"></i>);

    const { isModalOpen, packagesMenuOpen, packagesFilterText, packagesMenuRect } = this.state;
    // Add Shipment Package subform → package selection only
    // (scm:inputtext name="cf_packages" windowname="Packages").
    // Same CustomAll options UI as layout Package subform.
    const isPackagesLookup = this.isPackagesLookupField();
    const selectedLookupValue = getLookupStoredValue(value);
    const packagesOptions = isSet(lookupAutoComplete, []).filter(
      (opt) => opt && String(opt.label || "").trim() !== ""
    );
    // console.log('displayName',displayName);

    // Packages only: layout CustomAll — closed value + caret; menu portaled to body
    // so it is not clipped inside the Package table/accordion container.
    if (isPackagesLookup) {
      const menuRect = packagesMenuRect || {};
      const packagesMenu =
        packagesMenuOpen &&
        menuRect.top != null &&
        typeof document !== "undefined" &&
        document.body
          ? ReactDOM.createPortal(
              <div
                id={this.packagesMenuId}
                className="subformTable"
                style={{
                  position: "fixed",
                  top: menuRect.top,
                  left: menuRect.left,
                  width: 265,
                  zIndex: 2000,
                }}
                onMouseDown={this.stopPackagesEvent}
                onClick={this.stopPackagesEvent}
              >
                <div
                  className="customized-control"
                  style={{
                    width: 265,
                    height: "auto",
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    display: "block",
                  }}
                >
                  <div
                    className="dropdown-menu show customHide"
                    style={{
                      position: "static",
                      display: "block",
                      float: "none",
                      width: 265,
                      maxWidth: 265,
                      paddingTop: 0,
                      margin: 0,
                      transform: "none",
                      background: "#fff",
                      border: "1px solid rgba(0,0,0,.15)",
                      borderRadius: "0.25rem",
                      boxShadow: "0 0.5rem 1rem rgba(0,0,0,.15)",
                    }}
                  >
                    <div className="clearfix">
                      <div className="p-2 border-0">
                        <input
                          type="text"
                          ref={this.packagesSearchRef}
                          className="form-control form-control-sm"
                          style={{ width: "100%" }}
                          value={packagesFilterText}
                          onChange={this.handlePackagesSearch}
                          onClick={this.stopPackagesEvent}
                          onMouseDown={this.stopPackagesEvent}
                          placeholder="Search here...."
                          autoComplete="off"
                        />
                      </div>
                      <div
                        className="p-0"
                        style={{
                          maxHeight: 220,
                          overflow: "auto",
                          background: "#fff",
                        }}
                      >
                        <div className="card-list">
                          {isLoading ? (
                            <div className="dropdown-item" style={{ fontSize: 13, color: "#999" }}>
                              Loading...
                            </div>
                          ) : packagesOptions.length > 0 ? (
                            packagesOptions.map((opt, index) => {
                              const isSelected =
                                selectedLookupValue != null &&
                                selectedLookupValue !== "" &&
                                String(opt.value) === String(selectedLookupValue);
                              return (
                                <div
                                  key={opt.value || index}
                                  className={`cursor-pointer dropdown-item${
                                    isSelected ? " option-selected" : ""
                                  }`}
                                  onClick={(ev) => this.handlePackagesOptionSelect(ev, opt)}
                                  onMouseDown={this.stopPackagesEvent}
                                >
                                  <span className="text-truncate d-block">{opt.label}</span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="dropdown-item" style={{ fontSize: 13, color: "#999" }}>
                              <em>Not found..</em>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null;

      return (
        <Wrp>
          <div
            className="input-group position-static subformTable"
            style={{ position: "relative", zIndex: packagesMenuOpen ? 30 : 5 }}
            onMouseDown={this.stopPackagesEvent}
          >
            <input
              type="hidden"
              value={selectedLookupValue}
              name={attr.name}
              id={attr.id}
              autoComplete="new-lookup"
            />
            <div
              ref={this.packagesControlRef}
              className="customized-control"
              style={{
                position: "relative",
                flex: "1 1 auto",
                width: "1%",
                minWidth: 120,
                cursor: attr.readOnly ? "default" : "pointer",
              }}
              onMouseDown={this.stopPackagesEvent}
              onClick={this.togglePackagesMenu}
            >
              <div className="componenetInner text-truncate w-100">
                {displayName || ""}
              </div>
              <span className="custom-select cursor-pointer" />
            </div>
            {packagesMenu}
            {!(attr.readOnly || false) ? (
              <div
                className={
                  required === true
                    ? "input-group-append cursor-pointer mandatory"
                    : "input-group-append cursor-pointer"
                }
                onMouseDown={this.stopPackagesEvent}
                onClick={(e) => {
                  this.stopPackagesEvent(e);
                  this.openLoopupModalHandler();
                }}
              >
                <span className="input-group-text">
                  {customLookupIcon ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: renderNodeChildren(customLookupIcon),
                      }}
                    />
                  ) : (
                    defaultIcon
                  )}
                </span>
              </div>
            ) : null}
          </div>
          <LookupModal
            isModalOpen={isModalOpen}
            windowName={windowName}
            selectedValue={value}
            childrenProps={childrenProps}
            fieldFormPayload={this.props.fieldFormPayload}
            cancelLoopupModalHandler={this.cancelLoopupModalHandler}
            includeRecord={
              !!this.props.fill ||
              !!(this.props.attributes && this.props.attributes.fill) ||
              this.props.name === "to_address" ||
              this.isPackagesLookupField()
            }
            setLookupValuesFromList={this.setLookupValuesFromList}
          />
        </Wrp>
      );
    }

    return (
      <Wrp>
        <div className="input-group autocomplete">
          <input
            type="hidden"
            value={getLookupStoredValue(value)}
            {...attr}
            autoComplete="new-lookup"
          />
          <Autocomplete
            attributes={attr}
            suggestions={lookupAutoComplete}
            search={this.autoCompleteSearch}
            select={this.autoCompleteSelect}
            selectedValue={displayName}
            isDisable={attrLocked}
            isLoading={isLoading}
            windowName={windowName}
          />
          {!attrLocked ? (
            <div
              className={required === true ? "input-group-append cursor-pointer mandatory" : "input-group-append cursor-pointer"}
              onClick={this.openLoopupModalHandler}
            >
              <a className="input-group-text">
                {customLookupIcon ? (
                  <span dangerouslySetInnerHTML={{ __html: renderNodeChildren(customLookupIcon) }} />
                ) : (
                  defaultIcon
                )}
              </a>
            </div>
          ) : null}
        </div>
        <LookupModal
          isModalOpen={isModalOpen}
          windowName={windowName}
          selectedValue={value}
          childrenProps={childrenProps}
          fieldFormPayload={this.props.fieldFormPayload}
          cancelLoopupModalHandler={this.cancelLoopupModalHandler}
          includeRecord={
            !!this.props.fill ||
            !!(this.props.attributes && this.props.attributes.fill) ||
            this.props.name === "to_address" ||
            this.isPackagesLookupField()
          }
          setLookupValuesFromList={this.setLookupValuesFromList}
        />
      </Wrp>
    );
  }
}

PagesLookup.defaultProps = {
  isMandatory: false,
  isEvent: false,
  className: "form-control custom-arrow",
  required: false,
};

const mapDispatchToProps = (dispatch) => ({
  Loader: (bool) => dispatch(actions.loader(bool)),
});

export default connect(null, mapDispatchToProps)(PagesLookup);
